import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

const STATUS_VARIANT = { OPEN: 'warning', PAID: 'success', CANCELLED: 'neutral' };
const LEVEL_LABEL = { 1: '1', 2: '2', 3: '3' };

export function DunningPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overdue');

  const overdue = useQuery({
    queryKey: ['finance', 'dunning-overdue'],
    queryFn: () => financeApi.dunningOverdue().then((r) => r.items.invoices),
  });
  const notices = useQuery({
    queryKey: ['finance', 'dunning'],
    queryFn: () => financeApi.listDunning().then((r) => r.items),
  });

  const createNotice = useMutation({
    mutationFn: (invoiceId) => financeApi.createDunning({ invoiceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'dunning-overdue'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'dunning'] });
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }) => financeApi.setDunningStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'dunning'] }),
  });

  const overdueRows = overdue.data || [];
  const noticeRows = notices.data || [];
  const totalOutstanding = overdueRows.reduce((acc, r) => acc + (Number(r.outstanding) || 0), 0);
  const openNotices = noticeRows.filter((n) => n.status === 'OPEN').length;

  const overdueColumns = [
    {
      key: 'invoiceNumber',
      label: t('dunning.col.invoice'),
      render: (r) => <Link to={`/finance/invoices/${r.invoiceId}`}>{r.invoiceNumber}</Link>,
    },
    { key: 'dueDate', label: t('col.due'), render: (r) => formatDate(r.dueDate) },
    { key: 'daysOverdue', label: t('dunning.col.daysOverdue'), align: 'right', render: (r) => `${r.daysOverdue}` },
    { key: 'outstanding', label: t('dunning.col.outstanding'), align: 'right', render: (r) => formatCurrency(r.outstanding) },
    {
      key: 'lastLevel',
      label: t('dunning.col.lastLevel'),
      render: (r) => (r.lastLevel > 0 ? <Badge variant="info">{t('dunning.level')} {r.lastLevel}</Badge> : <span className="muted">-</span>),
    },
    {
      key: 'actions',
      label: '',
      render: (r) =>
        r.lastLevel < 3 ? (
          <Button size="sm" variant="primary" onClick={() => createNotice.mutate(r.invoiceId)} disabled={createNotice.isPending}>
            {t('dunning.action.create')} {r.lastLevel + 1}
          </Button>
        ) : (
          <span className="muted">{t('dunning.maxReached')}</span>
        ),
    },
  ];

  const noticeColumns = [
    { key: 'noticeDate', label: t('dunning.col.date'), render: (r) => formatDate(r.noticeDate) },
    {
      key: 'invoiceNumber',
      label: t('dunning.col.invoice'),
      render: (r) => <Link to={`/finance/dunning/${r.id}/print`}>{r.invoiceNumber}</Link>,
    },
    { key: 'level', label: t('dunning.col.level'), render: (r) => <Badge variant="info">{t('dunning.level')} {LEVEL_LABEL[r.level]}</Badge> },
    { key: 'amountDue', label: t('dunning.col.outstanding'), align: 'right', render: (r) => formatCurrency(r.amountDue, r.currency) },
    { key: 'fee', label: t('dunning.col.fee'), align: 'right', render: (r) => formatCurrency(r.fee, r.currency) },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`dunning.status.${r.status}`)}</Badge> },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Link className="btn primary sm" to={`/finance/dunning/${r.id}/print`}>
            {t('dunning.action.view')}
          </Link>
          {r.status === 'OPEN' && (
            <>
              <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: r.id, status: 'PAID' })}>
                {t('dunning.action.markPaid')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setStatus.mutate({ id: r.id, status: 'CANCELLED' })}>
                {t('dunning.action.cancel')}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('dunning.title')}</h1>
          <p className="muted">{t('dunning.subtitle')}</p>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('dunning.metric.overdue')} value={String(overdueRows.length)} />
        <MetricCard label={t('dunning.metric.outstanding')} value={formatCurrency(totalOutstanding)} trendVariant="danger" />
        <MetricCard label={t('dunning.metric.openNotices')} value={String(openNotices)} />
        <MetricCard label={t('dunning.metric.totalNotices')} value={String(noticeRows.length)} />
      </div>

      <div className="tab-bar">
        <button type="button" className={`tab${tab === 'overdue' ? ' tab-active' : ''}`} onClick={() => setTab('overdue')}>
          {t('dunning.tab.overdue')}
        </button>
        <button type="button" className={`tab${tab === 'notices' ? ' tab-active' : ''}`} onClick={() => setTab('notices')}>
          {t('dunning.tab.notices')}
        </button>
      </div>

      {tab === 'overdue' ? (
        <Table columns={overdueColumns} rows={overdueRows} isLoading={overdue.isLoading} emptyMessage={t('dunning.empty.overdue')} onRowClick={(r) => navigate(`/finance/invoices/${r.invoiceId}`)} />
      ) : (
        <Table columns={noticeColumns} rows={noticeRows} isLoading={notices.isLoading} emptyMessage={t('dunning.empty.notices')} onRowClick={(r) => navigate(`/finance/dunning/${r.id}/print`)} />
      )}
    </section>
  );
}
